import { useState, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '@/lib/axios';
import { SmartIdentifierInput } from '@/components/ui/smart-identifier-input';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormPasswordField } from '@/components/forms/form-password-field';
import { FormOtpField } from '@/components/forms/form-otp-field';
import { toast } from '@/lib/toast';
import { useTenant } from '@/providers/tenant-context';
import { Loader2, ArrowRight, Lock, Mail, Phone, KeyRound } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthPolicyNotice } from '@/components/auth/AuthPolicyNotice';
import { clearLegacyLoginStorageKeys } from '@/lib/clear-legacy-login-storage';
import { markPendingNotificationPromptIfNative } from '@/lib/notification-prompt-state';
import {
  getActiveInviteCode,
  persistInviteCode,
  resolvePostAuthDestination,
  resolveSafeRedirectPath,
} from '@/lib/invite-flow';

const identifierSchema = z.object({
  identifier: z.string().min(1, 'Email or Phone is required'),
});

const loginSchema = z.object({
  identifier: z.string(),
  password: z.string().min(1, 'Password is required'),
});

const otpSchema = z.object({
  identifier: z.string(),
  otp: z.string().length(6, 'OTP must be 6 digits'),
});

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('inviteCode');
  const { tenant, isWhitelabel } = useTenant();

  useEffect(() => {
    clearLegacyLoginStorageKeys();
  }, []);

  useEffect(() => {
    if (inviteCode) {
      persistInviteCode(inviteCode);
    }
  }, [inviteCode]);

  const activeInviteCode = getActiveInviteCode(inviteCode);
  const redirectPath = resolveSafeRedirectPath(searchParams.get('redirect'));

  const [step, setStep] = useState<'check' | 'login'>(() => {
    return (sessionStorage.getItem('login_step') as 'check' | 'login') || 'check';
  });
  const [loginMethod, setLoginMethod] = useState<'password' | 'otp'>(() => {
    return (
      (sessionStorage.getItem('login_method') as 'password' | 'otp') ||
      (localStorage.getItem('loginMethod') as 'password' | 'otp') ||
      'password'
    );
  });
  const [identifierType, setIdentifierType] = useState<'email' | 'phone'>(() => {
    return (sessionStorage.getItem('login_identifier_type') as 'email' | 'phone') || 'email';
  });
  const [otpSent, setOtpSent] = useState(() => {
    return sessionStorage.getItem('login_otp_sent') === 'true';
  });
  const [resendTimer, setResendTimer] = useState(() => {
    const savedTime = sessionStorage.getItem('login_resend_timer');
    if (savedTime) {
      const elapsed = Math.floor((Date.now() - parseInt(savedTime, 10)) / 1000);
      return Math.max(0, 60 - elapsed);
    }
    return 0;
  });

  const [isLoading, setIsLoading] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);

  const checkForm = useForm<z.infer<typeof identifierSchema>>({
    resolver: zodResolver(identifierSchema),
    defaultValues: {
      identifier: '',
    },
  });

  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      identifier: '',
      password: '',
    },
  });

  const otpForm = useForm<z.infer<typeof otpSchema>>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      identifier: '',
      otp: '',
    },
  });

  useEffect(() => {
    sessionStorage.setItem('login_step', step);
    sessionStorage.setItem('login_method', loginMethod);
    sessionStorage.setItem('login_identifier_type', identifierType);
    sessionStorage.setItem('login_otp_sent', otpSent.toString());
  }, [step, loginMethod, identifierType, otpSent]);

  const clearSessionState = () => {
    sessionStorage.removeItem('login_step');
    sessionStorage.removeItem('login_method');
    sessionStorage.removeItem('login_identifier_type');
    sessionStorage.removeItem('login_otp_sent');
    sessionStorage.removeItem('login_resend_timer');
  };

  async function onCheckSubmit(values: z.infer<typeof identifierSchema>) {
    setIsLoading(true);
    setOtpSent(false);
    try {
      const isEmail = values.identifier.includes('@');
      setIdentifierType(isEmail ? 'email' : 'phone');

      const payload = isEmail ? { email: values.identifier } : { phone: values.identifier };

      const response = await axiosInstance.post('/api/auth/check', payload);

      if (response.data.exists && !response.data.needsRegistration) {
        setStep('login');

        loginForm.setValue('identifier', values.identifier);
        otpForm.setValue('identifier', values.identifier);

        loginForm.setValue('password', '');

        if (loginMethod === 'otp') {
          await sendOtp(values.identifier);
        }
      } else {
        const params = new URLSearchParams();
        const prefill: {
          firstName?: string;
          middleName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
        } = response.data.needsRegistration ? response.data.user || {} : {};

        const email = prefill.email || (isEmail ? values.identifier : '');
        const phone = prefill.phone || (!isEmail ? values.identifier : '');
        if (email) params.set('email', email);
        if (phone) params.set('phone', phone);
        if (prefill.firstName) params.set('firstName', prefill.firstName);
        if (prefill.middleName) params.set('middleName', prefill.middleName);
        if (prefill.lastName) params.set('lastName', prefill.lastName);
        if (activeInviteCode) {
          params.set('inviteCode', activeInviteCode);
        }
        if (redirectPath) {
          params.set('redirect', redirectPath);
        }
        if (response.data.needsRegistration) {
          toast.info('Complete your registration', {
            description: 'Your account exists but has no password yet. Set one to continue.',
          });
        }
        navigate(`/register?${params.toString()}`);
      }
    } catch (error) {
      console.error(error);
      toast.error('Error', { description: 'Failed to check account status.' });
    } finally {
      setIsLoading(false);
    }
  }

  async function onLoginSubmit(values: z.infer<typeof loginSchema>) {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post('/api/auth/login', {
        identifier: values.identifier,
        password: values.password,
        inviteCode: activeInviteCode || undefined,
      });

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('loginMethod', 'password');
        const destination = resolvePostAuthDestination({
          joinedCommunityId: response.data.joinedCommunityId,
          fallback: redirectPath || '/dashboard',
        });
        clearSessionState();
        markPendingNotificationPromptIfNative();
        window.location.href = destination;
      }
    } catch (_error) {
      toast.error('Login Failed', { description: 'Invalid credentials.' });
    } finally {
      setIsLoading(false);
    }
  }

  async function sendOtp(identifier?: string) {
    setIsLoading(true);
    try {
      const trimmed = (identifier ?? '').trim();
      const id =
        trimmed ||
        loginForm.getValues('identifier').trim() ||
        otpForm.getValues('identifier').trim() ||
        checkForm.getValues('identifier').trim();

      if (!id) {
        toast.error('Missing email or phone', {
          description: 'Go back and enter your identifier.',
        });
        return;
      }

      await axiosInstance.post('/api/auth/login/otp/send', { identifier: id });
      setOtpSent(true);
      setResendTimer(60);
      sessionStorage.setItem('login_resend_timer', Date.now().toString());
      toast.success('OTP Sent', { description: 'Check your email and WhatsApp.' });
      setOtpError(null);
    } catch (_error) {
      toast.error('Failed to send OTP', { description: 'Please try again.' });
    } finally {
      setIsLoading(false);
    }
  }

  async function onOtpSubmit(values: z.infer<typeof otpSchema>) {
    setIsLoading(true);
    setOtpError(null);
    try {
      const response = await axiosInstance.post('/api/auth/login/otp/verify', {
        identifier: values.identifier,
        otp: values.otp,
        inviteCode: activeInviteCode || undefined,
      });

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('loginMethod', 'otp');
        const destination = resolvePostAuthDestination({
          joinedCommunityId: response.data.joinedCommunityId,
          fallback: redirectPath || '/dashboard',
        });
        clearSessionState();
        markPendingNotificationPromptIfNative();
        window.location.href = destination;
      }
    } catch (_error) {
      setOtpError('Invalid code. Please try again.');
      toast.error('Login Failed', { description: 'Invalid or expired OTP.' });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      sessionStorage.removeItem('login_resend_timer');
    }
  }, [resendTimer]);

  const watchedOtp = otpForm.watch('otp');
  useEffect(() => {
    if (otpError) setOtpError(null);
    // Intentionally omit `otpError` — we only want to clear when the user types.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedOtp]);

  const toggleLoginMethod = () => {
    if (loginMethod === 'password') {
      setLoginMethod('otp');
      if (!otpSent) {
        sendOtp();
      }
    } else {
      setLoginMethod('password');
    }
  };

  /** Mirrors login identifier for Chromium/password-manager pairing (same form as password). */
  const credentialUsername = useWatch({
    control: loginForm.control,
    name: 'identifier',
    defaultValue: '',
  });

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8 bg-background">
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        <Card className="w-full border-0 md:border md:border-border bg-background/50 backdrop-blur-md shadow-none md:shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              {isWhitelabel && tenant?.logoWithName ? (
                <img
                  src={tenant.logoWithName}
                  alt={tenant.name}
                  className="h-12 w-auto object-contain"
                />
              ) : (
                <img
                  src={isWhitelabel && tenant?.logo ? tenant.logo : '/milora-logo.png'}
                  alt={isWhitelabel ? tenant?.name : 'Milora'}
                  className="w-12 h-12 object-contain"
                />
              )}
            </div>
            <CardTitle className="text-2xl">Welcome</CardTitle>
            <CardDescription>Enter your details to continue</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <AnimatePresence mode="wait">
              {step === 'check' && (
                <motion.div
                  key="check"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <Form {...checkForm}>
                    <form
                      onSubmit={checkForm.handleSubmit(onCheckSubmit)}
                      autoComplete="on"
                      className="space-y-6"
                    >
                      <FormField
                        control={checkForm.control}
                        name="identifier"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email or Phone</FormLabel>
                            <FormControl>
                              <SmartIdentifierInput
                                value={field.value}
                                onValueChange={field.onChange}
                                onBlur={field.onBlur}
                                name={field.name}
                                autoComplete="username"
                                id="login-identifier-step-check"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="submit"
                        className="w-full h-11 text-base font-semibold"
                        disabled={isLoading}
                      >
                        {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue'}
                        {!isLoading && <ArrowRight className="ml-2 w-4 h-4" />}
                      </Button>
                    </form>
                  </Form>
                </motion.div>
              )}

              {step === 'login' && (
                <motion.div
                  key="login"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <div className="mb-6 bg-accent/5 p-4 rounded-xl flex items-center justify-between border border-border/5">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        {identifierType === 'email' ? (
                          <Mail className="w-4 h-4 text-primary" />
                        ) : (
                          <Phone className="w-4 h-4 text-primary" />
                        )}
                      </div>
                      <span className="font-medium truncate text-sm">
                        {loginForm.getValues('identifier') || checkForm.getValues('identifier')}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={() => {
                        checkForm.setValue(
                          'identifier',
                          loginForm.getValues('identifier') || checkForm.getValues('identifier')
                        );
                        setStep('check');
                        setOtpSent(false);
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Change
                    </Button>
                  </div>

                  {loginMethod === 'password' ? (
                    <Form {...loginForm}>
                      <form
                        onSubmit={loginForm.handleSubmit(onLoginSubmit)}
                        autoComplete="on"
                        className="space-y-6"
                      >
                        {/*
                          Not shown: paired with visible password so browsers can save/fill credentials
                          without displaying the identifier field alongside the password (shared register UX).
                          Keep off-screen, not display:none — better heuristics for password managers.
                        */}
                        <input
                          type="text"
                          name="username"
                          autoComplete="username"
                          tabIndex={-1}
                          readOnly
                          value={credentialUsername}
                          aria-hidden="true"
                          className="sr-only pointer-events-none"
                        />
                        <FormPasswordField
                          control={loginForm.control}
                          name="password"
                          label="Password"
                          autoFocus
                          autoComplete="current-password"
                          helper={
                            <Link
                              to={`/forgot-password?identifier=${encodeURIComponent(
                                loginForm.getValues('identifier') ||
                                  checkForm.getValues('identifier') ||
                                  ''
                              )}`}
                              className="text-xs font-medium text-primary hover:underline"
                            >
                              Forgot password?
                            </Link>
                          }
                        />
                        <Button
                          type="submit"
                          className="w-full h-11 text-base font-semibold"
                          disabled={isLoading}
                        >
                          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Log in'}
                        </Button>

                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-muted/20" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase tracking-wide">
                            <span className="bg-card px-2 text-muted-foreground">Or</span>
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          className="w-full h-11"
                          onClick={toggleLoginMethod}
                          disabled={isLoading}
                        >
                          <KeyRound className="mr-2 h-4 w-4" />
                          Login via OTP
                        </Button>
                      </form>
                    </Form>
                  ) : (
                    <div className="space-y-6">
                      <div className="text-center text-sm text-muted-foreground px-4">
                        We sent a verification code to your registered email and WhatsApp.
                      </div>

                      <Form {...otpForm}>
                        <form onSubmit={otpForm.handleSubmit(onOtpSubmit)} className="space-y-6">
                          <FormOtpField
                            control={otpForm.control}
                            name="otp"
                            onComplete={() => otpForm.handleSubmit(onOtpSubmit)()}
                          />
                          {otpError && (
                            <p className="text-center text-sm font-medium text-destructive">
                              {otpError}
                            </p>
                          )}

                          <Button
                            type="submit"
                            className="w-full h-11 text-base font-semibold"
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              'Verify & Login'
                            )}
                          </Button>

                          <div className="text-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground"
                              onClick={() => sendOtp()}
                              disabled={isLoading || resendTimer > 0}
                            >
                              {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                            </Button>
                          </div>

                          <Button
                            type="button"
                            variant="outline"
                            className="w-full h-11"
                            onClick={toggleLoginMethod}
                          >
                            <Lock className="mr-2 h-4 w-4" />
                            Login via Password
                          </Button>
                        </form>
                      </Form>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
            <AuthPolicyNotice variant="login" />
          </CardContent>
        </Card>

        <p className="mt-8 text-center text-xs text-muted-foreground">
          Powered by <span className="font-medium text-foreground/80">Milora</span>
        </p>
      </motion.div>
    </div>
  );
}
